/**
 * Authentication constants for the single Cognito advisor pool.
 *
 * Per STRUCTURE.md D2: one pool, role-based authz from cognito:groups.
 * Values come from cognito.generated.ts (built from config.yaml).
 */

import { COGNITO } from './cognito.generated.js';

export const AUTH_CONFIG = {
  region: COGNITO.region,
  userPoolId: COGNITO.userPoolId,
  clientId: COGNITO.clientId,
  domain: COGNITO.domain,
  get redirectUri(): string {
    // BLUF-976 / ADR-039: Cognito redirects to the API now, not the UI.
    // The API exchanges the code, creates the server-side session, and
    // redirects the browser back into the UI. See callback.ts.
    return `${window.location.origin}/api/auth/callback`;
  },
  get logoutUri(): string {
    // Cognito redirects here AFTER it has cleared its own session. The
    // API's logout endpoint is called separately (POST /api/auth/logout)
    // before this redirect, so the session record and cookies are gone
    // by the time Cognito sends us back to `/`.
    return `${window.location.origin}/`;
  },
} as const;

export function getLoginUrl(): string {
  // BLUF-976: the API now owns the start of the OAuth flow so it can
  // generate and persist the OAuth `state` parameter (CSRF defence
  // for the redirect flow). The UI just navigates to /api/auth/login;
  // the API issues a 302 to Cognito's hosted UI with state embedded.
  return '/api/auth/login';
}

export function getLogoutUrl(): string {
  const { domain, clientId, logoutUri } = AUTH_CONFIG;
  if (!domain || !clientId) return '';
  return `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
}

/**
 * BLUF-1323 — role names come from the live Cognito catalogue at
 * `GET /api/auth/role-catalogue`, not a hardcoded union. Kept as a
 * `string` alias (rather than raw `string`) so grep for `CanonicalRole`
 * still lands on the auth-side type. Consumers that need a specific
 * role name (e.g. `RequireRole allowedRoles={['Underwriters']}`) can
 * pass literal strings; the compile-time exhaustiveness that the old
 * union pretended to give was a lie once the taxonomy grew server-
 * side, so this is a truthfulness improvement, not a type regression.
 */
export type CanonicalRole = string;

export type UserGroup = CanonicalRole;

/**
 * BLUF-1323 — role catalogue payload from `GET /api/auth/role-catalogue`.
 * Mirrors the RoleDefinition shape exported by
 * api/src/auth/role-catalogue.ts. Consumed by SelectRolePage
 * (BLUF-1323) to render the picker tiles.
 */
export interface RoleDefinition {
  name: CanonicalRole;
  precedence: number;
  description: string;
  isInternal: boolean;
  isAdvisorFacing: boolean;
  /**
   * Post-login landing path parsed from the Cognito group description's
   * `[landing: /path]` marker. Null when the marker is absent — callers
   * that need a fallback (e.g. AccessDeniedPage's "Return to Home")
   * should default to `/` rather than guessing another role's page.
   */
  landingPath: string | null;
}

